import { AxiosError, isAxiosError } from "axios";
import { useParams, useRouter } from "next/navigation";
import { useCallback } from "react";

import { useUserContext } from "@/context/user.context";
import { type User, UserRole } from "@/types/user";
import type { Nullable } from "@/types/utils";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";
import { screenerIdIsNew } from "@/utils/screener-builder";

export const getScreenerBuilderEnabled = (user: Nullable<User>) => {
  if (!user) {
    return false;
  }

  const { isInternal, role } = user;

  const screenerBuilderEnabledRoles = new Set([
    UserRole.Editor,
    UserRole.Admin,
  ]);

  return isInternal || screenerBuilderEnabledRoles.has(role);
};

// Enables editing within the screener builder. View-only is available to all
// users.
export const useScreenerBuilderEnabled = () => {
  const { user } = useUserContext();

  return user ? getScreenerBuilderEnabled(user) : false;
};

// Simple hook to grab ID off params and check whether it === "new"
export const useScreenerParamsWithNewCheck = () => {
  const params = useParams<{ id?: string }>();

  if (!params || !params.id) {
    throw Error(
      "useScreenerParamsWithNewCheck failed to find an ID parameter. Is it being called from within the screener builder?",
    );
  }

  return [params.id, screenerIdIsNew(params.id)] as const;
};

export const useGetScreenerBuilderApiErrorHandler = (
  // Passing a status with no override means this hook will simply ignore errors
  // of that status. Passing "5xx" to status will allow overriding all errors in
  // the 500 range
  overrides: {
    override?: (e: AxiosError) => void;
    status: number | "5xx";
  }[] = [],
) => {
  const router = useRouter();

  const handler = useCallback(
    (err: Error) => {
      if (isAxiosError(err) && err.status) {
        const override = overrides.find(
          ({ status }) =>
            status === err.status ||
            (status === "5xx" && (err.status ?? 0) >= 500),
        );

        if (override) {
          return override.override?.(err);
        }

        if (err.status < 500) {
          switch (err.status) {
            case 401:
              showUpwageNotification(
                "Session expired. Please log in again.",
                NotificationTypes.Error,
              );

              return router.push("/auth/login");
            case 403:
              showUpwageNotification(
                "You don't have permission to edit this interviewer",
                NotificationTypes.Error,
              );
              return router.push("/");
            case 404:
              showUpwageNotification(
                "The interviewer you're trying to edit was not found",
                NotificationTypes.Error,
              );
              return router.push("/screener-builder/new/position");
          }
        }
      }

      showUpwageNotification(
        "There was a problem communicating with the server",
        NotificationTypes.Error,
      );
    },
    [overrides, router],
  );

  return handler;
};
